import { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { DragAndDropContext, useDragAndDrop } from "@iventis/drag-and-drop";
import { useContext, useRef, useEffect } from "react";

export const useTreeBrowserDragAndDrop = <TData extends { id: string }>(
    data: TData,
    isDraggingNode: boolean,
    isDragAllowed = true
): [(element: HTMLElement) => void, DraggableAttributes, DraggableSyntheticListeners] => {
    const { newParentId, bottomItemId, setOverElement, overElement, setAnchoredToTop } = useContext(DragAndDropContext);

    const dropZoneRef = useRef<HTMLElement>();

    const [dragAndDropRefs, attributes, listeners] = useDragAndDrop(data);

    const setRefs = (element: HTMLElement) => {
        if (isDragAllowed) {
            dragAndDropRefs(element);
        }
        dropZoneRef.current = element;
    };

    // Calculate the upper bound of the dropzone
    useEffect(() => {
        if (setOverElement && setAnchoredToTop) {
            // Only set the upper bound of the dropzone to this element if the ids match
            if (data.id === newParentId) {
                setOverElement(dropZoneRef?.current);
                setAnchoredToTop(true);
            } else if (newParentId === undefined) {
                // If the parentId is undefined (meaning we're hovered over nothing) set the drop position to be undefined
                setOverElement(undefined);
            }
        }
    }, [newParentId, isDraggingNode, overElement]);

    // Calculate the bottom item
    useEffect(() => {
        if (setOverElement && setAnchoredToTop) {
            // Only set the over element as the bottom item if its undefined from the top element but we have a parent id
            if (data.id === bottomItemId && newParentId != null) {
                setOverElement(dropZoneRef?.current);
                // Only anchor to the bottom if the bottom id is not equal to the top id
                if (data.id !== newParentId) {
                    setAnchoredToTop(false);
                }
            }
        }
    }, [bottomItemId, isDraggingNode, overElement]);

    // return undefinied if dragging is not allowed, prevents node elements subscribing to drag and drop events
    return [setRefs, isDragAllowed ? attributes : undefined, isDragAllowed ? listeners : undefined];
};
